import api from 'helpers/api'
import { parseCsvColumns, parseCsvRows, streamCsvChunks } from '../modal/views/append/helpers'

// Rows are created in batches rather than uploading the whole file in one
// request, so a large CSV is never sent as a single huge body and the import
// can report progress. This mirrors the streaming import used when appending
// a CSV into an existing table.
const ROW_LIMIT = 2500

// A problem with the CSV itself that the user can fix. Its message is written for the user and is
// shown as-is; any other error falls back to the generic failure message.
export class CsvImportError extends Error {}

// A row is blank when it has no cells or every cell is empty/whitespace after
// unquoting — so rows like `,,` and `" "," "` are skipped, matching the
// backend's _is_blank_row (which sees already-unquoted values).
const isBlankCells = (cells: string[]): boolean =>
  !cells || cells.length === 0 || cells.every((cell) => unquoteCsvValue(cell).trim() === '')

// Excel and other tools prefix CSVs with a UTF-8 BOM. The backend strips it via
// utf-8-sig; we must strip it too or it pollutes the first header/value.
const stripBom = (text: string): string => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text)

// Strip outer CSV quotes then unescape doubled quotes. Order matters: stripping
// the outer delimiters first ensures the '""' -> '"' conversion doesn't touch
// them. Mirrors the unescaping done by the append import.
export const unquoteCsvValue = (value: string): string => {
  if (value.length >= 2 && value.charAt(0) === '"' && value.charAt(value.length - 1) === '"') {
    value = value.substring(1, value.length - 1)
  }
  return value.replaceAll('""', '"')
}

// parseCsvColumns (shared with the append import) drops the single trailing
// empty field when a row ends with an unquoted delimiter, e.g. "a,b," -> ["a","b"]
// instead of ["a","b",""]. That under-counts columns when deriving from the
// header and can silently drop the last value of later rows, so restore it.
export const splitCsvRowCells = (rowText: string): string[] => {
  const cells = parseCsvColumns(rowText)
  let inQuotes = false
  for (let i = 0; i < rowText.length; i++) {
    const ch = rowText[i]
    if (ch === '"') {
      if (inQuotes && rowText[i + 1] === '"') i++
      else inQuotes = !inQuotes
    }
  }
  if (!inQuotes && rowText.endsWith(',')) cells.push('')
  return cells
}

// Build text column definitions from the first non-blank CSV row. Mirrors the
// backend create_table_from_csv: header values become column names, otherwise
// columns are named "Column 0", "Column 1", ... — every column is kind "text".
export const buildColumnsFromFirstRow = (
  firstRowCells: string[],
  hasHeader: boolean
): Array<{ name: string; kind: string }> => {
  if (!hasHeader) {
    return firstRowCells.map((_, idx) => ({ name: `Column ${idx}`, kind: 'text' }))
  }
  const seen = new Set<string>()
  return firstRowCells.map((cell) => {
    const name = unquoteCsvValue(cell).trim()
    if (name === '') {
      throw new CsvImportError(
        "One of the column headers in your CSV is blank. Give every column a header, or untick 'Use first row as column headers'."
      )
    }
    if (seen.has(name)) {
      throw new CsvImportError(
        `Your CSV has more than one column called '${name}'. Give each column a different header, then upload it again.`
      )
    }
    seen.add(name)
    return { name, kind: 'text' }
  })
}

// Map a row's cells to rowData keyed by column name (the shape createNewRows expects).
export const buildRowData = (cells: string[], columnNames: string[]): Record<string, unknown> => {
  const rowData: Record<string, unknown> = {}
  for (let j = 0; j < columnNames.length; j++) {
    rowData[columnNames[j]] = unquoteCsvValue(cells[j] ?? '')
  }
  return rowData
}

// Read the first non-blank row from the file, scanning across chunks (the
// backend scans until the first non-blank row, so a file that starts with many
// blank rows must not throw just because they span the first chunk).
const readFirstRowCells = async (file: File): Promise<string[]> => {
  if (file.size === 0) throw new CsvImportError('This CSV file is empty. Choose a file with at least one row.')
  let isFirstChunk = true
  for await (const rawChunk of streamCsvChunks(file)) {
    const chunk = isFirstChunk ? stripBom(rawChunk) : rawChunk
    isFirstChunk = false
    for (const row of parseCsvRows(chunk)) {
      const cells = splitCsvRowCells(row)
      if (!isBlankCells(cells)) return cells
    }
  }
  throw new CsvImportError('This CSV file only has blank rows. Choose a file with data in it.')
}

// Derive the text columns a new table should have from the CSV's first row.
export const deriveCsvColumns = async (
  file: File,
  hasHeader: boolean
): Promise<Array<{ name: string; kind: string }>> => {
  const firstRowCells = await readFirstRowCells(file)
  return buildColumnsFromFirstRow(firstRowCells, hasHeader)
}

export interface ImportProgress {
  current: number
  total: number
}

// Stream the file row-by-row and create rows in the given view in batches,
// skipping blank rows and (when present) the header row. onProgress is invoked
// after each batch with the batch count and an estimated total — the file is
// streamed so the true row count isn't known up front, so the total is
// extrapolated from the bytes seen so far (the same approach as the append
// import) and never reported below current. shouldCancel is checked after each
// batch; when it returns true streaming stops early, leaving the rows imported
// so far in place (the caller decides what to do with the partial table).
export const streamCsvRowsToView = async (
  file: File,
  projectId: string,
  viewId: string,
  columnNames: string[],
  hasHeader: boolean,
  onProgress?: (progress: ImportProgress) => void,
  shouldCancel?: () => boolean
): Promise<void> => {
  let batch: Array<{ rowData: Record<string, unknown> }> = []
  let isFirstRow = true
  let batchCount = 0
  let rowsSeen = 0
  let charsSeen = 0
  let estimatedBatches = 1

  const flush = async (): Promise<void> => {
    if (batch.length === 0) return
    await api.createNewRows(projectId, viewId, batch)
    batch = []
    batchCount++
    const estimatedTotalRows = charsSeen > 0 ? Math.round(rowsSeen * (file.size / charsSeen)) : rowsSeen
    estimatedBatches = Math.max(batchCount, Math.ceil(estimatedTotalRows / ROW_LIMIT))
    onProgress?.({ current: batchCount, total: estimatedBatches })
  }

  const iterator = streamCsvChunks(file)[Symbol.asyncIterator]()
  let result = await iterator.next()
  let isFirstChunk = true
  while (!result.done) {
    if (shouldCancel?.()) return
    const chunk = isFirstChunk ? stripBom(result.value) : result.value
    isFirstChunk = false
    charsSeen += chunk.length
    for (const csvRow of parseCsvRows(chunk)) {
      const cells = splitCsvRowCells(csvRow)
      if (isBlankCells(cells)) continue
      if (isFirstRow) {
        isFirstRow = false
        if (hasHeader) continue
      }
      // Reject ragged rows rather than silently padding/truncating, matching the
      // backend create_table_from_csv. The caller deletes the partial table.
      if (cells.length !== columnNames.length) {
        throw new CsvImportError(
          `A row in your CSV has ${cells.length} values but the table has ${columnNames.length} columns. Make every row the same length (look for extra commas), then upload it again.`
        )
      }
      rowsSeen++
      batch.push({ rowData: buildRowData(cells, columnNames) })
      if (batch.length >= ROW_LIMIT) {
        await flush()
        if (shouldCancel?.()) return
      }
    }
    result = await iterator.next()
  }
  await flush()
}
